import Code from "@/components/Code";
import {
  CheckIcon,
  ChevronDownIcon,
  Copy04Icon,
  Database01Icon,
  Download02Icon,
  TerminalIcon,
} from "@/icons";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/kit/ui/accordion";
import { Button } from "@/kit/ui/button";
import { Link } from "@/kit/ui/link";
import {
  ResponsiveMenu,
  ResponsiveMenuContent,
  ResponsiveMenuTrigger,
} from "@/kit/ui/responsive-menu";
import { toast } from "@/utils/toast";
import { MouseEventHandler, useState } from "react";
import { SiPandas, SiPolars } from "react-icons/si";
import { FormattedMessage } from "react-intl";
import { graphql, useFragment } from "react-relay";
import CodeViewer from "./CodeViewer";
import { DatasetUseButtonFragment$key } from "./__generated__/DatasetUseButtonFragment.graphql";
import { FormattedDataSize } from "./FormattedDataSize";
import { useClipboardAvailable } from "@/utils/hooks";

const DatasetUseButtonFragment = graphql`
  fragment DatasetUseButtonFragment on DatasetVersion {
    version
    files {
      nodes {
        size
        url
      }
    }
    dataset {
      slug
    }
  }
`;

interface Props {
  datasetVersion: DatasetUseButtonFragment$key;
}

const pythonPolarSnippet = (
  slug: string,
  version: string,
) => `import polars as pl
from aqora.pyarrow import dataset

df = pl.scan_pyarrow_dataset(dataset("${slug}", "v${version}")).collect()
`;

const pythonPandasSnippet = (
  slug: string,
  version: string,
) => `import pandas as pd
df = pd.read_parquet("aqora://${slug}/v${version}")
`;

const cliCommandSnippet = (slug: string, version: string) =>
  `aqora dataset download --version ${version} ${slug}`;

export function DatasetUseButton(props: Props) {
  const {
    files: { nodes: files },
    version,
    dataset: { slug },
  } = useFragment(DatasetUseButtonFragment, props.datasetVersion);

  return (
    <ResponsiveMenu snapPoints={[1]}>
      <ResponsiveMenuTrigger asChild>
        <Button>
          <Database01Icon className="w-5 h-5" />
          <FormattedMessage defaultMessage="Use dataset" />
          <ChevronDownIcon className="w-5 h-5" />
        </Button>
      </ResponsiveMenuTrigger>
      <ResponsiveMenuContent
        align="end"
        alignOffset={0}
        className="p-0 lg:min-w-(--radix-popper-anchor-width) w-full"
      >
        <Accordion
          type="multiple"
          className="p-2"
          defaultValue={["polar", "pandas", "croissant", "cli"]}
        >
          <AccordionItem value="polar">
            <AccordionTrigger className="lg:w-100 w-full">
              <span className="flex items-center gap-1">
                <SiPolars />
                <p className="text-sm font-medium text-grey-950">
                  <FormattedMessage defaultMessage="Polars" />
                </p>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-3 w-(--radix-accordion-content-width)">
              <Snippet content={pythonPolarSnippet(slug, version)} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="pandas">
            <AccordionTrigger className="lg:w-100 w-full">
              <span className="flex items-center gap-1">
                <SiPandas />
                <p className="text-sm font-medium text-grey-950">
                  <FormattedMessage defaultMessage="Pandas" />
                </p>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-3 w-(--radix-accordion-content-width)">
              <Snippet content={pythonPandasSnippet(slug, version)} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="cli">
            <AccordionTrigger className="lg:w-100 w-full">
              <span className="flex items-center gap-1">
                <TerminalIcon className="w-3.5 h-3.5" />
                <p className="text-sm font-medium text-grey-950">
                  <FormattedMessage defaultMessage="Aqora CLI" />
                </p>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-3 w-(--radix-accordion-content-width)">
              <Code content={cliCommandSnippet(slug, version)} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        {files.length === 1 && (
          <>
            <hr />
            <div className="p-2">
              <Link
                to={files[0].url}
                variant="secondary"
                className="w-full"
                target="_blank"
              >
                <Download02Icon />
                <FormattedMessage
                  defaultMessage="Download <small>({size})</small>"
                  values={{
                    size: <FormattedDataSize bytes={files[0].size} />,
                    small: (chunks) => (
                      <small className="text-sm text-grey-500">{chunks}</small>
                    ),
                  }}
                />
              </Link>
            </div>
          </>
        )}
      </ResponsiveMenuContent>
    </ResponsiveMenu>
  );
}

interface SnippetProps {
  content: string;
}

function Snippet({ content }: SnippetProps) {
  const [copied, setCopied] = useState(false);
  const clipboard = useClipboardAvailable();

  const onCopy: MouseEventHandler = (event) => {
    event.preventDefault();
    navigator.clipboard.writeText(content);
    setCopied(true);
    toast.success(<FormattedMessage defaultMessage="Copied!" />);
  };

  return (
    <div className="relative group lg:w-100 w-full">
      <div className="lg:w-100 w-full overflow-x-auto rounded-xl border border-grey-200">
        <CodeViewer
          language="python"
          children={content}
          wrapLongLines={false}
        />
      </div>
      {clipboard && (
        <Button
          onClick={onCopy}
          size="sm"
          variant="secondary"
          className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100"
          disabled={copied}
        >
          {copied ? (
            <CheckIcon className="h-4 w-4" />
          ) : (
            <Copy04Icon className="h-4 w-4" />
          )}
          <span className="sr-only">{copied ? "Copied" : "Copy"}</span>
        </Button>
      )}
    </div>
  );
}
